// ── The FINAL CALL: the corn yield prediction freezes on October 10 ──────────
//
// Kevin, 2026-10-20: "lets make october 10th our end date, freeze the yield
// prediction then so we can score it once we have final yields."
//
// Oct 10 is the median Western NY black layer — the last date the weather (a
// killing freeze before black layer) can legitimately move the call. Before this
// there was NO end date: mi_yield_daily kept charting into the harvest
// (2026-10-17: 160.6 / 167.1) and the call could never be scored, because there
// was no single number to score.
//
// One rule, every caller: both writers of mi_yield_daily (api/_mi-snapshot.js
// and the Yield page), the season window (api/_mi-season.js), the page's
// headline + chart, the daily brief, and the scoring panel. Dependency-free on
// purpose so the season module and the checker can load it without the model.
//
// THE FREEZE DAY IS INCLUSIVE. A row stamped Oct 10 (Eastern) is the final
// call; nothing stamped after it is written. The day is the Eastern day the
// writers already stamp rows with (miSnapshotDay) — never UTC.

const MI_FREEZE = { month: 10, day: 10 };   // October 10, inclusive

function pad2(n) { return String(n).padStart(2, "0"); }

// "YYYY-10-10" for a crop year. Throws on a year that is not a plausible crop
// year — a defaulted or NaN year would silently freeze the wrong season.
function miFreezeDate(cropYear) {
  const y = Number(cropYear);
  if (!Number.isInteger(y) || y < 2000 || y > 2100) throw new Error(`miFreezeDate: not a crop year: ${cropYear}`);
  return `${y}-${pad2(MI_FREEZE.month)}-${pad2(MI_FREEZE.day)}`;
}

// May a mi_yield_daily row for `cropYear` be stamped with Eastern day `day`?
// Only on or before the freeze. A row for a different calendar year is not this
// rule's question (the writers already refuse cross-year rows).
function miMayWriteDaily(cropYear, day) {
  return String(day) <= miFreezeDate(cropYear);
}

// Is the call for `cropYear` frozen as of Eastern day `day`? Strictly after.
function miIsFrozen(cropYear, day) {
  return String(day) > miFreezeDate(cropYear);
}

// Drop rows the chart must not draw: anything stamped after the freeze. Rows
// written before this rule existed (2026-10-11 .. 10-17) are KEPT in the table —
// deleting them is irreversible — and are filtered here instead.
function miClipToFreeze(rows, cropYear) {
  const f = miFreezeDate(cropYear);
  return (rows || []).filter((r) => r && r.snapshot_date && String(r.snapshot_date) <= f);
}

// The mi_yield_daily row the final call is taken FROM: the freeze day's row if
// it exists, else the latest row on or before it (a day nobody computed), and
// the source day is recorded either way so a reader can tell.
function miFreezeSourceRow(dailyRows, cropYear, region) {
  const f = miFreezeDate(cropYear);
  let best = null;
  for (const r of dailyRows || []) {
    if (!r || r.region !== region || r.predicted_yield == null) continue;
    if (Number(r.crop_year != null ? r.crop_year : cropYear) !== Number(cropYear)) continue;
    const d = String(r.snapshot_date);
    if (d > f) continue;
    if (!best || d > String(best.snapshot_date)) best = r;
  }
  return best;
}

// One mi_yield_final_call row for (cropYear, region).
//  - The REGIONAL figures come from the mi_yield_daily row (exact).
//  - The PER-COUNTY detail comes from a stored model result (`countyResult`,
//    already region-filtered by the caller with the shared miFilterResult). That
//    result is only the LATEST run, so when it was computed after the freeze
//    day the detail is flagged `county_detail_captured_late` and the note says
//    so — it is never presented as the freeze day's.
function miBuildFinalCallRow({ cropYear, region, dailyRows, countyResult, countyResultAt, capturedAt }) {
  const src = miFreezeSourceRow(dailyRows, cropYear, region);
  if (!src) return null;
  const freeze = miFreezeDate(cropYear);
  const counties = [];
  for (const c of (countyResult && countyResult.counties) || []) {
    if (!c || !c.fips || c.error) continue;
    counties.push({
      county: c.name || String(c.fips), fips: String(c.fips),
      pred_yield: c.forecast_yield != null ? Number(c.forecast_yield) : null,
      pred_acres: c.pred_acres != null ? Number(c.pred_acres) : null,
      pred_bu: c.pred_prod != null ? Math.round(Number(c.pred_prod)) : null,
    });
  }
  const detailDay = countyResultAt ? new Date(countyResultAt).toLocaleDateString("en-CA", { timeZone: "America/New_York" }) : null;
  const late = !detailDay || detailDay !== String(src.snapshot_date);
  return {
    crop_year: Number(cropYear), region,
    freeze_date: freeze,
    source_snapshot_date: String(src.snapshot_date),
    predicted_yield: src.predicted_yield != null ? Number(src.predicted_yield) : null,
    production_bu: src.production_bu != null ? Number(src.production_bu) : null,
    five_yr_avg_yield: src.five_yr_avg_yield != null ? Number(src.five_yr_avg_yield) : null,
    model_version: src.model_version || null,
    counties: counties.length ? counties : null,
    county_detail_captured_late: counties.length ? late : null,
    county_detail_as_of: counties.length ? detailDay : null,
    note: !counties.length
      ? "No per-county result was available to record."
      : late
        ? `Per-county detail is from the model run of ${detailDay || "an unknown day"}, not ${src.snapshot_date}. The model keeps only its latest run, so the ${src.snapshot_date} per-county result no longer exists. The regional figures are exact.`
        : null,
    captured_at: capturedAt || new Date().toISOString(),
  };
}

// Which final call the page shows for (cropYear, region): the recorded row when
// the table holds one, else the mi_yield_daily freeze row (identical regional
// figures — the record is copied from it). Null before the freeze, and null when
// there is nothing on or before the freeze day (a year the model never charted).
function miPickFinalCall({ cropYear, region, day, finalRows, dailyRows }) {
  if (!miIsFrozen(cropYear, day)) return null;
  const rec = (finalRows || []).find((r) => r && Number(r.crop_year) === Number(cropYear) && r.region === region && r.predicted_yield != null);
  if (rec) return { ...rec, recorded: true };
  const src = miFreezeSourceRow(dailyRows, cropYear, region);
  if (!src) return null;
  return {
    crop_year: Number(cropYear), region, freeze_date: miFreezeDate(cropYear),
    source_snapshot_date: String(src.snapshot_date),
    predicted_yield: Number(src.predicted_yield),
    production_bu: src.production_bu != null ? Number(src.production_bu) : null,
    production_pct: src.production_pct != null ? Number(src.production_pct) : null,
    five_yr_avg_yield: src.five_yr_avg_yield != null ? Number(src.five_yr_avg_yield) : null,
    model_version: src.model_version || null,
    counties: null, recorded: false,
  };
}

// ── SCORING: the frozen call against NASS's FINAL yields ─────────────────────
//
// What counts as a final, read off nass_data as the sync stores it:
//  - STATE final = stat_type 'YIELD' on the state row (county null). The current
//    year's in-season forecasts are stored as 'YIELD_FORECAST' and are NOT a
//    final; they must never score the call (the Sep-forecast overwrite this repo
//    already paid for once).
//  - COUNTY finals = stat_type 'YIELD' on a county row. "OTHER (COMBINED)
//    COUNTIES" (fips ending 998) is a pooled district figure, never a county,
//    and is excluded. Census-vs-survey is resolved at sync (preferSurvey).
//  - WNY = ACRE-WEIGHTED mean of the WNY counties' final yields, weighted by
//    each county's NASS final ACRES_HARVESTED. A county with no published final
//    yield or acreage (NASS suppression) is left out and NAMED — never filled.
function isFinalYieldRow(r) { return r && String(r.stat_type) === "YIELD"; }
function isStateRow(r) { return !r.county || String(r.county).trim() === ""; }
function isCombined(r) { return /998$/.test(String(r.fips || "")) || /OTHER/i.test(String(r.county || "")); }
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

function miScoreFinalCall({ cropYear, finalCalls, nassRows, wnyFips, modelFips }) {
  const yr = Number(cropYear);
  const rows = (nassRows || []).filter((r) => r && Number(r.year) === yr && String(r.crop || "CORN").toUpperCase() === "CORN");
  const stateRow = rows.find((r) => isFinalYieldRow(r) && isStateRow(r));
  const countyYield = new Map(), countyAcres = new Map();
  for (const r of rows) {
    if (isStateRow(r) || isCombined(r)) continue;
    const f = String(r.fips || "");
    if (!f) continue;
    if (isFinalYieldRow(r) && num(r.value) != null) countyYield.set(f, num(r.value));
    if (String(r.stat_type) === "ACRES_HARVESTED" && num(r.value) != null) countyAcres.set(f, num(r.value));
  }
  const call = (region) => (finalCalls || []).find((c) => c && c.region === region && Number(c.crop_year) === yr) || null;
  const nextYear = yr + 1;

  if (!stateRow && countyYield.size === 0) {
    return { available: false, absence: `NASS final yields for ${yr} are not published yet — state final expected ~Jan ${nextYear}, county finals ~Feb ${nextYear}.` };
  }

  const weighted = (fipsSet) => {
    let wSum = 0, ySum = 0; const used = [], missing = [];
    for (const f of fipsSet) {
      const y = countyYield.get(String(f)), a = countyAcres.get(String(f));
      if (y == null || a == null || a <= 0) { missing.push(String(f)); continue; }
      wSum += a; ySum += a * y; used.push(String(f));
    }
    return { yield: wSum > 0 ? ySum / wSum : null, counties_used: used.length, used_fips: used, counties_missing: missing, acres: wSum };
  };
  const err = (pred, act) => (pred != null && act != null ? Math.round((pred - act) * 10) / 10 : null);

  const ny = call("ny"), wny = call("wny");
  const wnyAct = weighted(wnyFips || []);
  const modelAct = weighted(modelFips || []);
  const stateFinal = stateRow ? num(stateRow.value) : null;

  const counties = [];
  const src = (ny && Array.isArray(ny.counties) ? ny.counties : []);
  for (const c of src) {
    const act = countyYield.get(String(c.fips));
    counties.push({ county: c.county, fips: String(c.fips), pred_yield: c.pred_yield, nass_final: act != null ? act : null, error: err(c.pred_yield, act) });
  }

  return {
    available: true,
    state_final_published: stateFinal != null,
    county_finals_published: countyYield.size > 0,
    ny: {
      frozen: ny ? ny.predicted_yield : null,
      nass_state_final: stateFinal,
      error_vs_state: err(ny && ny.predicted_yield, stateFinal),
      nass_modelled_counties: modelAct.yield != null ? Math.round(modelAct.yield * 10) / 10 : null,
      error_vs_modelled: err(ny && ny.predicted_yield, modelAct.yield),
      modelled_used: modelAct.counties_used, modelled_missing: modelAct.counties_missing,
    },
    wny: {
      frozen: wny ? wny.predicted_yield : null,
      nass_weighted: wnyAct.yield != null ? Math.round(wnyAct.yield * 10) / 10 : null,
      error: err(wny && wny.predicted_yield, wnyAct.yield),
      used: wnyAct.counties_used, used_fips: wnyAct.used_fips, missing: wnyAct.counties_missing,
      method: "acre-weighted mean of the WNY counties' NASS final yields, weighted by NASS final harvested acres",
    },
    counties,
    county_detail_captured_late: !!(ny && ny.county_detail_captured_late),
  };
}

module.exports = {
  MI_FREEZE, miFreezeDate, miMayWriteDaily, miIsFrozen, miClipToFreeze,
  miFreezeSourceRow, miBuildFinalCallRow, miPickFinalCall, miScoreFinalCall,
};
